/*
 * ************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *   Copyright 2018 Adobe Systems Incorporated
 *   All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by all applicable intellectual property
 * laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 * ************************************************************************
 */

import React from 'react';
import { connect } from '@airglow/store-redux';
import { selectConnectedClients, selectSocketConnected } from '@nebula/dorado-redux-storage';

import SessionView from '../../ui/session-view';

import {
  layouts,
  pluginConfig,
  validationPlugins
} from '../../casing/data/feature.flags';
import { workspacesOpen } from '../../casing/data/prefabs';

import SessionCreateModalContainer from '../../session-create';
import SessionDetailsModalContainer from './session.details.modal.container';
import ViewPluginsManagerContainer from './view.plugins.manager.container';

import { navigateToSessionList } from '../../common/actions';
import { selectActiveSessionName, selectEnabledViewPlugins } from '../../common/selectors';
import {
  pluginEndpoint,
  railOpen,
  selectedLayout,
  sessionNotFound,
  showConfigure,
  showDetails,
  watchShellSettingsChanges
} from '../data/prefabs';
import * as A from '../actions';
import {
  getSelectedViewNamespace,
  selectConfgureWorkspacesEnabled,
  selectViewPluginCatalog,
  selectViewPluginsGrouped
} from '../selectors';
import SettingsView from '../../settings-view';
import { settingsOpen, settingsView } from '../../settings-view/data/prefabs';
import Workspaces from '../../workspaces';

const mapState = (state, props) => {
  const selectedLayoutValue = selectedLayout.value(state);
  const selectedView = getSelectedViewNamespace(state);
  const configureWorkspacesEnabled = selectConfgureWorkspacesEnabled(state);

  return {
    availableViewPlugins: selectViewPluginCatalog(state),
    configureWorkspacesEnabled,
    connectedClients: selectConnectedClients(state),
    createWidget: <SessionCreateModalContainer {...props} />,
    detailsWidget: <SessionDetailsModalContainer {...props} />,
    enableValidationPlugins: validationPlugins(state),
    isConfigure: showConfigure.isOn(state),
    pluginEndpointReady: !!pluginEndpoint.value(state),
    railOpen: railOpen.isOpen(state),
    selectedLayout: selectedLayoutValue,
    sessionNotFound: sessionNotFound.isSet(state),
    selectedView,
    sessionName: selectActiveSessionName(state),
    settingsWidget: <SettingsView {...props} />,
    socketConnected: selectSocketConnected(state),
    useConfigure: pluginConfig(state) && !configureWorkspacesEnabled,
    useLayouts: layouts(state),
    viewPlugins: selectViewPluginsGrouped(state),
    viewPluginsManagerWidget: <ViewPluginsManagerContainer
      {...props}
      plugins={selectEnabledViewPlugins(state)}
      selectedLayout={selectedLayoutValue}
      selectedView={selectedView}
    />,
    workspacesWidget: <Workspaces {...props} />
  };
};

const mapHandlers = dispatch => ({
  onApplyConfigToOrg: () => dispatch(A.applyViewPluginsToOrg()),
  onApplyConfigToUser: () => dispatch(A.applyViewPluginsToUser()),
  onBreadcrumbClick: ({ value }) => {
    if (value === 'home') {
      dispatch(navigateToSessionList());
    }
  },
  onConfigurePlugins: () => dispatch(showConfigure.toggleAction),
  onUpdatePluginConfig: v => dispatch(A.updatePluginConfig(v)),
  onEndSessionClick: () => {
    dispatch(navigateToSessionList());
  },
  onOpenSettings: () => {
    dispatch(showConfigure.turnOffAction);
    dispatch(watchShellSettingsChanges.turnOnAction);
    dispatch(workspacesOpen.turnOnAction);
    dispatch(settingsView.changeAction('viewPlugins'));
    dispatch(settingsOpen.turnOnAction);
  },
  onSelectView: path => dispatch(A.selectView(path)),
  onSessionDetailsClick: () => showDetails.doOpen(dispatch),
  onToggleRail: () => dispatch(railOpen.toggleAction)
});

export default connect(mapState, mapHandlers)(SessionView);
